import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import { formatIsoDate, subtractDay } from '@src/shared/lib';
import { useMutation, useQuery } from '@src/shared/query';
import { urlToQueryKey } from '@src/shared/query/utils';
import {
  type AbsenceTimeline,
  type AbsenceTimelineDto,
  EMPTY_TIMELINE,
  selectTimeline,
} from '../../lib/blocks';
import type { Absence } from '../../types';
import {
  absenceApproveHrUrl,
  absenceApproveUrl,
  absenceDeleteUrl,
  absenceDenyUrl,
  absenceRequestUrl,
  absenceTimelineUrl,
  absenceUrl,
  absenceWarningsUrl,
  absenceWithdrawApproveUrl,
  absenceWithdrawDenyUrl,
  absenceWithdrawUrl,
  groupStatsUrl,
  vacationBalanceUrl,
} from './constants';

export function invalidateAbsenceQueries() {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(absenceUrl)],
    }),
    // A separate key from the plain list, so it needs naming in its own right
    // -- a prefix match does not reach past the differing path segment.
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(absenceTimelineUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(absenceWarningsUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(groupStatsUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(vacationBalanceUrl)],
    }),
  ]);
}

export type AbsenceQueryParams = Partial<{
  date_from: string;
  date_to: string;
  user_id: string;
}>;

type AbsenceQueryRange = {
  start: Date;
  end: Date;
};

/**
 * The days the calendar asks for: exactly the days it draws, both ends
 * inclusive.
 *
 * `end` is exclusive, the way the calendar's own range is, so the last day
 * asked for is the day before it.
 *
 * There used to be a day of padding at each end. It was there so a bar at the
 * edge could be told whether it carried on, back when the shape came from
 * looking at whichever neighbouring rows happened to be loaded. Nothing infers
 * that any more: a block reaching past these dates comes back whole, with its
 * position stamped from its real membership, so a padding day would only buy
 * bars that cannot be drawn.
 *
 * Widening is driven off these dates -- a block crosses an edge if and only if
 * it has a row *on* that edge -- so narrowing them moves the test, it does not
 * weaken it.
 */
function loadedRange({ start, end }: AbsenceQueryRange): {
  from: string;
  to: string;
} {
  return { from: formatIsoDate(start), to: formatIsoDate(subtractDay(end, 1)) };
}

export function getAbsenceQueryParams(
  range: AbsenceQueryRange,
): AbsenceQueryParams {
  const { from, to } = loadedRange(range);
  return { date_from: from, date_to: to };
}

/**
 * Whether the rows in hand are this window's own answer.
 *
 * Three states, and only the last of them is safe to select against:
 *
 * - nothing for this window yet (`isSuccess` false). The calendar draws the new
 *   days with no absences behind them.
 * - the previous window's answer, kept while this one is fetched
 *   (`isPlaceholderData`). The days drawn are the new ones; the rows are old.
 * - this window's answer.
 *
 * The first state used to be indistinguishable from the third: the query
 * supplied `initialData`, so an unanswered window reported success with an empty
 * payload -- and with data always present, `placeholderData` never engaged
 * either. Both of the unsafe states read as "loaded".
 */
export function absencesAreLoaded(state: {
  isSuccess: boolean;
  isPlaceholderData: boolean;
}): boolean {
  return state.isSuccess && !state.isPlaceholderData;
}

/**
 * How the timeline query behaves, apart from which window it is for.
 *
 * Exported so the behaviour can be driven in a test through the same object the
 * hook uses, rather than by asserting what the hook is believed to pass.
 *
 * Note what is *not* here: `initialData`. An empty payload for a window nobody
 * has answered is not an answer, and supplying one made an unanswered window
 * report success -- and, with data always present, stopped `placeholderData`
 * from ever engaging. Both unsafe states then read as "loaded". Consumers get
 * `EMPTY_TIMELINE` from the default below, so nothing has to handle
 * `undefined`.
 */
export const absenceTimelineQueryBehaviour = {
  select: selectTimeline,
  placeholderData: (previousData: AbsenceTimelineDto | undefined) =>
    previousData,
} as const;

export default function useAbsences(queryParams: AbsenceQueryParams = {}) {
  const query = useQuery<AbsenceTimelineDto, AbsenceTimeline>(
    absenceTimelineUrl,
    {
      queryParams,
      ...absenceTimelineQueryBehaviour,
    },
  );

  const { mutateAsync: bookAbsence } = useMutation<
    { absences: Absence[] },
    Error,
    Record<string, unknown> & { headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      headers,
      ...rest
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: absenceUrl,
        method: 'POST',
        headers: headers as Record<string, string> | undefined,
        payload: rest,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: deleteAbsences } = useMutation<
    { absences: Absence[] },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceDeleteUrl,
        method: 'DELETE',
        headers,
        payload: ids,
        bodyOnDelete: true,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: requestAbsences } = useMutation<
    { requested: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceRequestUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ requested: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: withdrawAbsences } = useMutation<
    { withdrawn: number; requested: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ withdrawn: number; requested: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveWithdrawalAbsences } = useMutation<
    { withdrawn: number; forwarded: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawApproveUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ withdrawn: number; forwarded: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: denyWithdrawalAbsences } = useMutation<
    { denied: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawDenyUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ denied: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveHrAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveHrUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: denyAbsences } = useMutation<
    { denied: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceDenyUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ denied: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { data: timeline = EMPTY_TIMELINE, ...restQuery } = query;
  return {
    absences: timeline.absences,
    absencesLoaded: absencesAreLoaded(query),
    blocks: timeline.blocks,
    blockByAbsenceId: timeline.blockByAbsenceId,
    // What an edit or a decision may be scoped to, and the token it goes back
    // with. Absent means cannot act -- never "the block is one entry".
    entries: timeline.entries,
    entryByAbsenceId: timeline.entryByAbsenceId,
    bookAbsence,
    deleteAbsences,
    requestAbsences,
    withdrawAbsences,
    approveWithdrawalAbsences,
    denyWithdrawalAbsences,
    approveAbsences,
    approveHrAbsences,
    denyAbsences,
    ...restQuery,
  };
}
